import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const idempotencyKey = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)
const reasonCode = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/)
const quantity = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
const base = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
}

export const replenishmentPolicyPublishV1Schema = z
  .object({
    ...base,
    policyId: entityIdSchema,
    revisionId: entityIdSchema,
    expectedVersion: z.number().int().nonnegative(),
    locationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    minimumQuantityMilli: quantity,
    maximumQuantityMilli: quantity.positive(),
    leadTimeDays: z.number().int().min(0).max(365),
    demandHorizonDays: z.number().int().min(1).max(365),
    transferFirst: z.boolean(),
    reasonCode,
  })
  .strict()
  .refine(
    (value) => value.maximumQuantityMilli > value.minimumQuantityMilli,
    "Maximum must exceed minimum.",
  )

export const replenishmentEvaluateV1Schema = z
  .object({
    ...base,
    suggestionId: entityIdSchema,
    policyId: entityIdSchema,
    expectedVersion: z.literal(0),
    expectedPolicyVersion: z.number().int().positive(),
    asOfDate: z.string().date(),
    demandQuantityMilli: quantity,
    reasonCode,
  })
  .strict()

export const replenishmentPolicyResultV1Schema = z
  .object({
    entityType: z.literal("replenishment_policy"),
    entityId: entityIdSchema,
    state: z.literal("active"),
    version: z.number().int().positive(),
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    locationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    minimumQuantityMilli: quantity,
    maximumQuantityMilli: quantity.positive(),
    leadTimeDays: z.number().int().min(0).max(365),
    demandHorizonDays: z.number().int().min(1).max(365),
    transferFirst: z.boolean(),
  })
  .strict()

export const replenishmentSuggestionResultV1Schema = z
  .object({
    entityType: z.literal("replenishment_suggestion"),
    entityId: entityIdSchema,
    state: z.literal("suggested"),
    version: z.literal(1),
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    policyId: entityIdSchema,
    policyVersion: z.number().int().positive(),
    locationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    action: z.enum(["none", "transfer_suggestion", "purchase_request_suggestion"]),
    recommendedQuantityMilli: quantity,
    transferLocationId: entityIdSchema.nullable(),
    transferQuantityMilli: quantity,
    neededByDate: z.string().date(),
    actualQuantityMilli: z.number().int(),
    reservedQuantityMilli: quantity,
    availableQuantityMilli: z.number().int(),
    orderedQuantityMilli: quantity,
    requestedQuantityMilli: quantity,
    demandQuantityMilli: quantity,
    minimumQuantityMilli: quantity,
    maximumQuantityMilli: quantity,
    explanation: z.array(z.string().min(1).max(200)).min(1).max(12),
    approvalRequired: z.literal(true),
    createdPurchaseRequest: z.literal(false),
    createdTransfer: z.literal(false),
  })
  .strict()

export type ReplenishmentPolicyPublishV1 = z.infer<
  typeof replenishmentPolicyPublishV1Schema
>
export type ReplenishmentEvaluateV1 = z.infer<typeof replenishmentEvaluateV1Schema>
export type ReplenishmentPolicyResultV1 = z.infer<
  typeof replenishmentPolicyResultV1Schema
>
export type ReplenishmentSuggestionResultV1 = z.infer<
  typeof replenishmentSuggestionResultV1Schema
>
